import { type JSX, useState } from 'react';
import Box from 'carbon-react/esm/components/box';
import {
  FlatTable,
  FlatTableBody,
  FlatTableCell,
  FlatTableHead,
  FlatTableHeader,
  FlatTableRow,
} from 'carbon-react/esm/components/flat-table';
import Icon from 'carbon-react/esm/components/icon';
import Typography from 'carbon-react/esm/components/typography';
import styled from 'styled-components';
import { useProfilesContext } from '../../../../context/useProfilesContext';
import { useResponsive } from '../../../../hooks/useResponsive';
import { useRoles } from '../../../../hooks/useRoles';
import type { AccountRole } from '../../../../types/profiles.types';
import AdminLoaderItems from '../AdminLoaderItems';
import AdminErrorItems from '../AdminErrorItems';

// Carbon Box exposes no border prop, so keep the panel outline in this small wrapper.
const ProfileDetailsPanel = styled(Box)`
  border: 1px solid var(--colorsUtilityMajor100);
`;

// FlatTableRow has no hover-color prop, so tint selected and hovered rows with Sage tokens.
const ProfileSelectableRow = styled(FlatTableRow)<{ $isSelected: boolean }>`
  && td {
    ${({ $isSelected }) => $isSelected && 'background-color: var(--colorsActionMajor150);'}
  }

  &&:hover td {
    background-color: ${({ $isSelected }) =>
      $isSelected ? 'var(--colorsActionMajor150)' : 'var(--colorsActionMajor025)'};
  }
`;

const getRoleColumns = (roles: AccountRole[], isMobileOrTablet: boolean): AccountRole[][] => {
  if (isMobileOrTablet || roles.length < 2) return [roles];

  const firstColumnSize = Math.ceil(roles.length / 2);
  return [roles.slice(0, firstColumnSize), roles.slice(firstColumnSize)];
};

const ProfilesOverview = (): JSX.Element => {
  const { profiles, loading: profilesLoading, error: profilesError } = useProfilesContext();
  const { roles, isLoading: rolesLoading, error: rolesError } = useRoles();
  const { isMobileOrTablet } = useResponsive();
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);

  if (profilesLoading || rolesLoading) {
    return <AdminLoaderItems message="Chargement des profils..." />;
  }

  if (profilesError || rolesError) {
    return <AdminErrorItems error={profilesError || rolesError} />;
  }

  const selectedProfile =
    profiles.find(profile => profile.id === selectedProfileId) ?? profiles[0] ?? null;
  const roleColumns = getRoleColumns(roles, isMobileOrTablet);

  if (profiles.length === 0) {
    return (
      <Box role="status" p={3}>
        Aucun profil disponible.
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection={isMobileOrTablet ? 'column' : 'row'}
      alignItems="stretch"
      gap={2}
      width="100%"
      minWidth={0}
    >
      <Box width={isMobileOrTablet ? '100%' : '32%'} minWidth={isMobileOrTablet ? 0 : 280}>
        <FlatTable
          width="100%"
          colorTheme="light"
          hasOuterVerticalBorders={false}
          aria-label="Profils disponibles"
        >
          <FlatTableHead>
            <FlatTableRow>
              <FlatTableHeader>Libellé</FlatTableHeader>
            </FlatTableRow>
          </FlatTableHead>
          <FlatTableBody>
            {profiles.map((profile, index) => {
              const isSelected = selectedProfile?.id === profile.id;

              return (
                <ProfileSelectableRow
                  key={profile.id}
                  $isSelected={isSelected}
                  onClick={() => setSelectedProfileId(profile.id)}
                  selected={isSelected}
                  aria-label={`${profile.name}${index === 0 ? ', premier profil' : ''}`}
                >
                  <FlatTableCell>{profile.name}</FlatTableCell>
                </ProfileSelectableRow>
              );
            })}
          </FlatTableBody>
        </FlatTable>
      </Box>

      {selectedProfile && (
        <ProfileDetailsPanel
          role="region"
          aria-label={`Droits du profil ${selectedProfile.name}`}
          aria-live="polite"
          bg="white"
          borderRadius="borderRadius100"
          p={3}
          flex="1"
          minWidth={0}
          minHeight={240}
        >
          <Typography variant="h3" mb={2}>
            {selectedProfile.name}
          </Typography>
          <Box height="1px" bg="var(--colorsUtilityMajor100)" mb={2} />
          <Typography variant="h4" mb={2}>
            Droits
          </Typography>

          {roles.length > 0 ? (
            <Box display="flex" flexDirection={isMobileOrTablet ? 'column' : 'row'} gap={3}>
              {roleColumns.map((column, columnIndex) => (
                <Box key={`role-column-${columnIndex}`} role="list" flex="1" minWidth={0}>
                  {column.map(role => {
                    const isActive = selectedProfile.rights.has(role.InstanceId);

                    return (
                      <Box
                        key={role.InstanceId}
                        role="listitem"
                        aria-label={`${role.Label} : ${isActive ? 'accordé' : 'non accordé'}`}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        mb={1}
                      >
                        <Box
                          width={32}
                          height={32}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                          aria-hidden="true"
                        >
                          {isActive && (
                            <Icon type="tick" size="small" color="positive" aria-hidden={true} />
                          )}
                        </Box>
                        <span>{role.Label}</span>
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
          ) : (
            <Box role="status">Aucun droit disponible.</Box>
          )}
        </ProfileDetailsPanel>
      )}
    </Box>
  );
};

export default ProfilesOverview;
